import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, firstValueFrom } from 'rxjs';
import { Device } from 'src/app/_models/device';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { DeviceService } from 'src/app/_services/device.service';
import { ModalService } from 'src/app/_services/modal.service';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ErrorPageComponent } from '../../_components/error-page/error-page.component';
import { IconButtonComponent } from '../../_components/buttons/icon-button/icon-button.component';
import { UnderlinedFormFieldComponent } from 'src/app/_components/forms/underlined-form-field/underlined-form-field.component';
import { macAddressValidator } from 'src/app/_helpers/mac-address-validator.validator';
import { AuthEventService } from 'src/app/_services/auth-event.service';

@Component({
  selector: 'app-add-device',
  templateUrl: './add-device.component.html',
  styleUrls: ['./add-device.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    UnderlinedFormFieldComponent,
    IconButtonComponent,
    ErrorPageComponent,
    ModalComponent,
  ],
})
export class AddDeviceComponent implements OnInit, OnDestroy {
  private readonly deviceService = inject(DeviceService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly authEventService = inject(AuthEventService);

  readonly currentUser = toSignal(this.authenticationService.currentUser$);
  readonly error = signal<ErrorObject | undefined>(undefined);
  readonly isProcessing = signal(false);
  readonly selectedLaboratory = signal<Laboratory | undefined>(undefined);
  readonly allowNavigation = new Subject<boolean>();

  nameField = new FormBase({
    key: 'name',
    label: 'Name of device',
    type: 'text',
    placeholder: 'Name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  macAddressField = new FormBase({
    key: 'macAddress',
    label: 'MAC Address',
    type: 'text',
    placeholder: 'XX:XX:XX:XX:XX:XX',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  createDeviceForm = this.formBuilder.group({
    name: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    macAddress: ['', [Validators.required, macAddressValidator()]],
  });

  ngOnInit(): void {
    // Check for cached form on init (after login redirect)
    const unsavedCachedData =
      this.deviceService.retrieveAndClearUnsavedDeviceCache;

    if (unsavedCachedData) {
      this.updateFormValues(unsavedCachedData);
    }

    this.selectedLaboratory.set(this.authenticationService.selectedLaboratory);
  }

  addDevice(): void {
    if (this.isProcessing()) {
      return;
    }

    this.error.set(undefined);
    this.isProcessing.set(true);

    this.deviceService.createDevice(this.getCurrentFormValues()).subscribe({
      next: (deviceId: number) => {
        this.isProcessing.set(false);
        this.createDeviceForm.markAsPristine();
        void this.router.navigate([`/device/${deviceId}/details`]);
      },
      error: (error: ErrorObject) => {
        this.isProcessing.set(false);
        this.error.set(error);
        this.openModal('error');
      },
    });
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'close-button':
        this.modalService.close('error');
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.modalService.close('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.modalService.close('attention');
        break;
    }
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  updateFormValues(device: Device): void {
    this.createDeviceForm.patchValue({
      name: device.name,
      macAddress: device.macAddress,
    });

    this.createDeviceForm.markAsDirty();
  }

  getCurrentFormValues(): Device {
    // hardwareVersion, installedSoftwareVersion and softwareWarning are left undefined
    // (normalised to null by the Device constructor): a newly created device hasn't
    // reported them yet - it only does so after its first power cycle.
    return new Device(
      undefined,
      this.createDeviceForm.value.name,
      this.selectedLaboratory()?.laboratoryId,
      this.createDeviceForm.value.macAddress,
    );
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  ngOnDestroy(): void {
    // Cache form if we're being destroyed due to 401 navigation
    if (
      this.authEventService.isUnauthorizedNavigationPending &&
      !this.createDeviceForm.pristine
    ) {
      const formValues = this.getCurrentFormValues();
      this.deviceService.onUpdateCreateDeviceCache(formValues);
    }

    // Settle any pending navigation guard Promise as "cancel" so
    // firstValueFrom below never rejects with EmptyError (which would
    // otherwise silently discard unsaved changes) on a destroy race.
    this.allowNavigation.next(false);
    this.allowNavigation.complete();
  }

  private isNavigationAllowed(): Promise<boolean> {
    if (
      this.createDeviceForm.pristine ||
      !this.currentUser() ||
      this.authEventService.isUnauthorizedNavigationPending
    ) {
      return Promise.resolve(true);
    }

    this.openModal('attention');
    return firstValueFrom(this.allowNavigation);
  }
}
